import { useMemo } from 'react';
import type { SalesContact } from '../types';

/**
 * Client-side refinement over the server-filtered contact list, shaped like
 * `features/orders/hooks/useOrderFiltering.ts`.
 *
 * Why both halves exist: order-service's SalesContactQueryDto handles stage/buildStatus/
 * source/solutionInterest/assignedRepId/q, so those go over the wire — but it has no
 * pagination and no sort, and its `q` matches only name/businessName/email. This hook adds
 * the extra fields a sales rep actually searches by (Instagram handle, phone, website) and
 * the sort, without a second round trip.
 */
export type ContactSort = 'recent' | 'name' | 'followers';

export const useContactFiltering = (
  contacts: SalesContact[],
  searchTerm: string,
  sort: ContactSort,
) =>
  useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matched = term
      ? contacts.filter((contact) =>
          [
            contact.name,
            contact.businessName,
            contact.email,
            contact.igHandle,
            contact.phone,
            contact.website,
          ].some((field) => field?.toLowerCase().includes(term)),
        )
      : contacts;

    // Sorting a copy: the query cache hands back the same array instance on every render, and
    // sorting in place would mutate cached data.
    return [...matched].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'followers') return (b.igFollowerCount ?? -1) - (a.igFollowerCount ?? -1);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [contacts, searchTerm, sort]);
